/**
 * Shared deterministic discipline presentation derivation, used by BOTH
 * ConversationSidebar's per-conversation classification chip (short badge
 * label, via `disciplineBadge`) and ChatPanel's composer scope selector
 * (full display name, via `disciplineDisplayName`) -- one derivation of
 * WHICH scope a disciplineId represents, never two parallel copies of that
 * null/undefined/unmatched resolution.
 *
 * "GC" (disciplineId === null, explicitly) is the synthetic project-wide
 * scope's SHORT badge label -- a completely separate concept from a REAL
 * ai_knowledge_disciplines row that happens to be named "General
 * Conditions" (see the collision guard in `disciplineBadge` below).
 * `undefined` is NOT the same thing as `null` here: it means the value is
 * missing/malformed (e.g. a persisted conversation whose
 * searchScopeDisciplineId field didn't come back from the server) and must
 * fail closed to an unknown/invalid presentation -- never be displayed as
 * GC/General Conditions, which is a real, valid, explicit scope.
 */

// Short badge label for the synthetic project-wide scope -- used ONLY by
// ConversationSidebar's history badge (disciplineBadge). ChatPanel's
// composer never shows this; it always shows GEN_COND_FULL_NAME instead
// (see disciplineDisplayName), since the composer displays full discipline
// names, never abbreviations.
export const GEN_COND_LABEL = 'GC';

// Full display name for the synthetic project-wide scope -- used ONLY by
// ChatPanel's composer scope selector (disciplineDisplayName), both for the
// currently-selected value and the option offered when the selector opens.
export const GEN_COND_FULL_NAME = 'General Conditions';

// Fixed, distinct color for the synthetic project-wide badge/selector.
// Deliberately kept OUT of DISCIPLINE_COLOR_PALETTE below so the
// project-wide presentation is always visually recognizable no matter which
// discipline colors happen to be in use.
export const GEN_COND_COLOR = '#37474f';

// Fallback label/color for a disciplineId that doesn't match any known
// discipline (stale/deleted discipline row) OR is missing/malformed
// (undefined) -- distinct from both GEN_COND_COLOR/GEN_COND_LABEL and the
// palette below so an unknown/invalid value never masquerades as GEN COND
// or as any real discipline.
export const UNKNOWN_DISCIPLINE_LABEL = 'Unknown discipline';
export const UNKNOWN_DISCIPLINE_COLOR = '#757575';

// Small, fixed, MUI-theme-appropriate palette used only for REAL
// disciplines' badges. A discipline's color is picked deterministically
// from its id (see hashStringToIndex) so the SAME discipline always renders
// the SAME color across renders/reloads -- never randomly per render.
export const DISCIPLINE_COLOR_PALETTE = [
  '#1976d2', // blue
  '#2e7d32', // green
  '#ed6c02', // orange
  '#9c27b0', // purple
  '#00838f', // teal
  '#c62828', // red
  '#6d4c41', // brown
];

/** Stable (non-cryptographic) string hash -- sum of char codes mod
 * `modulus` -- used only to deterministically pick a palette index from a
 * discipline's id, never for anything security-sensitive. */
export const hashStringToIndex = (str, modulus) => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash += str.charCodeAt(i);
  }
  return hash % modulus;
};

/**
 * Derives a short, deterministic abbreviation for a REAL discipline's badge
 * from its name: uppercase, letters-only per word, the first 3 letters of
 * the first word plus (for a multi-word name) the first 4 letters of the
 * second word -- e.g. "Submittals" -> "SUB". Used only as the FALLBACK for a
 * discipline name not covered by KNOWN_SHORT_LABELS below -- doesn't need to
 * be perfect, just short, deterministic, and reasonably legible.
 */
export const deriveAbbreviation = (name) => {
  const words = (name || '')
    .toUpperCase()
    .split(/[^A-Z]+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 3);
  return `${words[0].slice(0, 3)} ${words[1].slice(0, 4)}`;
};

// Fixed short badge labels for the discipline names this history badge is
// specifically designed around, keyed by name (case-insensitive, trimmed) --
// not by id, since these are well-known taxonomy labels, not per-tenant
// data. A discipline name NOT listed here still gets a badge via the
// deriveAbbreviation fallback immediately below, so the badge remains
// generic for whatever other disciplines actually exist.
const KNOWN_SHORT_LABELS = {
  'general conditions': 'GC',
  mechanical: 'MECH',
  electrical: 'ELEC',
  plumbing: 'PLUM',
};

const shortLabelForName = (name) =>
  KNOWN_SHORT_LABELS[(name || '').trim().toLowerCase()] ||
  deriveAbbreviation(name);

/**
 * Resolves which scope a disciplineId represents -- shared by both
 * disciplineBadge (short label) and disciplineDisplayName (full name) so
 * the null/undefined/unmatched fail-closed determination lives in exactly
 * one place, never duplicated per label format.
 */
const resolveDiscipline = (disciplineId, disciplines) => {
  if (disciplineId === null) return { kind: 'project-wide' };
  if (disciplineId === undefined) return { kind: 'unknown' };
  const match = disciplines.find((d) => d.id === disciplineId);
  return match ? { kind: 'real', match } : { kind: 'unknown' };
};

/**
 * Resolves a badge (SHORT label + color) for a given disciplineId, for
 * ConversationSidebar's history chip:
 *   - EXPLICIT null -> the synthetic, fixed-color GC badge (project-wide).
 *     This is the only value that renders as GC.
 *   - undefined -> fails closed to the unknown/invalid presentation below.
 *     `undefined` is a distinct, missing/malformed state (e.g. a persisted
 *     conversation whose searchScopeDisciplineId field is absent) and must
 *     never be visually indistinguishable from the real, explicit GC scope.
 *   - a disciplineId matching no known discipline (stale/deleted row) ->
 *     the same unknown/invalid presentation as undefined -- both are
 *     "cannot resolve this to a real scope" cases, so they share one
 *     fallback rather than inventing a second one.
 *   - otherwise -> the matching real discipline's short label (from
 *     KNOWN_SHORT_LABELS, or deriveAbbreviation as a fallback) and
 *     id-hashed color.
 *
 * CRITICAL GUARD: if a real discipline's short label happens to exactly
 * equal GEN_COND_LABEL (e.g. a discipline literally named "General
 * Conditions" maps to "GC" via KNOWN_SHORT_LABELS above), that discipline's
 * badge falls back to its FULL NAME instead of the short label, so it can
 * never be visually indistinguishable from the synthetic project-wide GC
 * badge. This check is unconditional -- it runs regardless of how rare the
 * collision seems.
 */
export const disciplineBadge = (disciplineId, disciplines) => {
  const resolved = resolveDiscipline(disciplineId, disciplines);
  if (resolved.kind === 'project-wide') {
    return { label: GEN_COND_LABEL, color: GEN_COND_COLOR };
  }
  if (resolved.kind === 'unknown') {
    return { label: UNKNOWN_DISCIPLINE_LABEL, color: UNKNOWN_DISCIPLINE_COLOR };
  }
  const shortLabel = shortLabelForName(resolved.match.name);
  const label =
    shortLabel === GEN_COND_LABEL ? resolved.match.name : shortLabel;
  const color =
    DISCIPLINE_COLOR_PALETTE[
      hashStringToIndex(resolved.match.id, DISCIPLINE_COLOR_PALETTE.length)
    ];
  return { label, color };
};

/**
 * Resolves the FULL display name for a given disciplineId, for ChatPanel's
 * composer scope selector, which must never show an abbreviation -- neither
 * for its currently-selected value nor for the choices offered when it
 * opens. Shares the exact same null/undefined/unmatched fail-closed
 * resolution as disciplineBadge (via resolveDiscipline) -- only the label
 * FORMAT differs (full name vs short badge), never the underlying
 * determination of which scope a disciplineId represents.
 */
export const disciplineDisplayName = (disciplineId, disciplines) => {
  const resolved = resolveDiscipline(disciplineId, disciplines);
  if (resolved.kind === 'project-wide') return GEN_COND_FULL_NAME;
  if (resolved.kind === 'unknown') return UNKNOWN_DISCIPLINE_LABEL;
  return resolved.match.name;
};
